import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Search, Clock, Eye, TrendingUp, ArrowRight, Moon, Sun } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useTheme } from '../../context/ThemeContext';
import FeaturedVideoSection from '../../components/FeaturedVideoSection';
import Footer from '../../components/Footer';
import NewsletterSubscribe from '../../components/NewsletterSubscribe';

const BlogHomePage = () => {
  const { categorySlug } = useParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [trendingPosts, setTrendingPosts] = useState([]);
  const [latestPosts, setLatestPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [highlightedPostId, setHighlightedPostId] = useState(null);
  const { theme, toggleTheme } = useTheme();

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  // Helper function to get correct image URL (supports persistent storage)
  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://via.placeholder.com/800x600?text=No+Image';
    if (imagePath.startsWith('http')) return imagePath;
    
    // Handle different path formats:
    // 1. /api/uploads/blog/xxx - new persistent storage (already correct)
    // 2. /uploads/blog/xxx - old format, needs /api prefix
    // 3. /uploads/xxx - legacy general uploads
    // 4. /api/xxx - already has /api prefix
    
    let cleanPath = imagePath;
    
    if (imagePath.startsWith('/api/')) {
      // Already has /api prefix, use as-is
      cleanPath = imagePath;
    } else if (imagePath.startsWith('/uploads/')) {
      // Convert old /uploads/ to /api/uploads/ for Kubernetes routing
      cleanPath = imagePath.replace('/uploads/', '/api/uploads/');
    } else {
      // Add /api prefix if not present
      cleanPath = `/api${imagePath}`;
    }
    
    return `${API_BASE}${cleanPath}`;
  };

  useEffect(() => {
    // Scroll to top when category changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchBlogData();
  }, [categorySlug]);

  const fetchBlogData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching blog data...', { API_BASE, categorySlug });
      
      const categoryParam = categorySlug ? `&category=${categorySlug}` : '';
      
      // Fetch with individual error handling
      const fetchWithFallback = async (url, fallback = { posts: [] }) => {
        try {
          const response = await fetch(url, { timeout: 10000 });
          if (!response.ok) {
            console.error(`Failed to fetch ${url}: ${response.status}`);
            return fallback;
          }
          return await response.json();
        } catch (error) {
          console.error(`Error fetching ${url}:`, error);
          return fallback;
        }
      };
      
      const [postsRes, catsRes, featuredRes, trendingRes, latestRes] = await Promise.all([
        fetchWithFallback(`${API_BASE}/api/blog/posts?limit=50${categoryParam}`, { posts: [] }),
        fetchWithFallback(`${API_BASE}/api/blog/categories`, []),
        fetchWithFallback(`${API_BASE}/api/blog/posts?featured=true&limit=3${categoryParam}`, { posts: [] }),
        fetchWithFallback(`${API_BASE}/api/blog/posts?sort=views&limit=5`, { posts: [] }),
        fetchWithFallback(`${API_BASE}/api/blog/posts?sort=latest&limit=50${categoryParam}`, { posts: [] })
      ]);
      
      console.log('📊 Blog posts fetched:', postsRes);
      console.log('📂 Categories fetched:', catsRes);
      console.log('⭐ Featured posts:', featuredRes);
      console.log('🔥 Trending posts:', trendingRes);
      console.log('🆕 Latest posts:', latestRes);
      
      setPosts(postsRes.posts || []);
      setCategories(catsRes || []);
      setFeaturedPosts(featuredRes.posts || []);
      setTrendingPosts(trendingRes.posts || []);
      setLatestPosts(latestRes.posts || []);
      
      // Find current category
      if (categorySlug) {
        const cat = (catsRes || []).find(c => c.slug === categorySlug);
        setCurrentCategory(cat);
      } else {
        setCurrentCategory(null);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('❌ Error fetching blog data:', error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Filter latest posts based on search term
  const filteredLatestPosts = latestPosts.filter(post => {
    if (!searchTerm.trim()) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      post.title?.toLowerCase().includes(searchLower) ||
      post.excerpt?.toLowerCase().includes(searchLower) ||
      post.author_name?.toLowerCase().includes(searchLower)
    );
  });

  // Smooth scroll utility function
  const smoothScrollTo = (targetPosition, duration = 1500) => {
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    let startTime = null;

    const animation = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      
      // Easing function for smooth animation
      const ease = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      
      window.scrollTo(0, startPosition + distance * ease);
      
      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  };

  // Handle search button click or Enter key
  const handleSearch = () => {
    if (!searchTerm.trim()) return;
    
    // Find the first matching post
    const matchingPost = filteredLatestPosts[0];
    
    if (matchingPost) {
      const postElement = document.getElementById(`post-${matchingPost.id}`);
      if (postElement) {
        // Calculate target position (center of screen)
        const elementPosition = postElement.getBoundingClientRect().top + window.pageYOffset;
        const targetPosition = elementPosition - (window.innerHeight / 2) + (postElement.offsetHeight / 2);
        
        // Start smooth scroll animation
        smoothScrollTo(targetPosition, 1500);
        
        // Highlight the post after scroll animation completes
        setTimeout(() => {
          setHighlightedPostId(matchingPost.id);
          
          // Remove highlight after 4 seconds
          setTimeout(() => {
            setHighlightedPostId(null);
          }, 4000);
        }, 1600);
      }
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800/80 dark:bg-gray-800/80 backdrop-blur-md shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img 
                src={theme === 'dark' ? '/trainerlist-logo-dark.png' : '/trainerlist-logo.png'}
                alt="TrainerList" 
                className="h-10 sm:h-12 md:h-14 w-auto object-contain"
              />
            </Link>
            <nav className="flex items-center gap-6">
              <Link to="/" className="text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 font-semibold">Home</Link>
              <Link to="/trainers" className="text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 font-semibold">Trainers</Link>
              <Link to="/blog" className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-800 font-bold">Blog</Link>
              <Link to="/shop" className="text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 font-semibold">Shop</Link>
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5 text-yellow-500" />
                ) : (
                  <Moon className="w-5 h-5 text-gray-600" />
                )}
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-600 via-red-800 to-red-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-black mb-4">
            {currentCategory ? currentCategory.name : 'TrainerList Blog'}
          </h1>
          <p className="text-xl mb-8 opacity-90">
            {currentCategory ? currentCategory.description || 'Expert articles and advice' : 'Fitness tips, expert advice, and inspiring stories - One Goal Passion!'}
          </p>
          <div className="max-w-2xl mx-auto">
            <div className="relative flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="pl-12 pr-4 py-6 text-lg rounded-full w-full"
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={!searchTerm.trim()}
                className="px-8 py-6 bg-gradient-to-r from-red-600 to-red-800 text-white font-bold text-lg rounded-full hover:from-red-700 hover:to-red-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
              >
                Search
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Video Section - Only show on main blog page */}
      {!categorySlug && <FeaturedVideoSection />}

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Categories - Only show on main blog page */}
        {!categorySlug && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6 dark:text-gray-100">Categories</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((cat) => {
                // Icon mapping
                const iconMap = {
                  'other-sport-branches': '🏀',
                  'nutrition-recipes': '🥗',
                  'training-tips': '💪',
                  'muscle-building': '🏋️',
                  'fitness-canada': '🍁',
                  'sport-nutrition': '💊'
                };
                
                return (
                  <Link
                    key={cat.id}
                    to={`/blog/category/${cat.slug}`}
                    className="p-6 bg-white dark:bg-gray-800 rounded-2xl text-center hover:shadow-xl transition-all transform hover:scale-105 border-2 border-transparent hover:border-red-300 dark:hover:border-red-600"
                  >
                    <div className="text-4xl mb-2">{iconMap[cat.slug] || '📝'}</div>
                    <p className="font-semibold text-sm dark:text-gray-200">{cat.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{cat.post_count || 0} articles</p>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Back to all categories button - Show when category is selected */}
        {categorySlug && (
          <div className="mb-8">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors font-semibold"
            >
              ← Back to All Categories
            </Link>
          </div>
        )}

        {/* Featured Posts - Only show on main blog page */}
        {!categorySlug && featuredPosts.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold dark:text-gray-100">Featured Articles</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredPosts.map((post) => (
                <Card key={post.id} className="overflow-hidden hover:shadow-2xl transition-all transform hover:scale-105 border-2 border-transparent hover:border-red-300 dark:hover:border-red-600 dark:bg-gray-800 dark:border-gray-700">
                  <CardContent className="p-0">
                    <div className="relative aspect-video bg-gray-100 dark:bg-gray-900">
                      <img src={getImageUrl(post.cover_image)} alt={post.title} className="w-full h-full object-contain" />
                      <Badge className="absolute top-3 right-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white">Featured</Badge>
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-lg mb-2 line-clamp-2 hover:text-red-600 dark:hover:text-red-400 dark:text-gray-100">
                        <Link to={`/blog/post/${post.slug}`}>{post.title}</Link>
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">{post.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>{post.author_name}</span>
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {post.reading_time} min
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {post.views}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Trending & Latest Posts - Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Trending Posts (1/3) */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="flex items-center gap-2 mb-6">
                <TrendingUp className="w-6 h-6 text-orange-500" />
                <h2 className="text-2xl font-bold dark:text-gray-100">TRENDING</h2>
              </div>
              
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <div key={n} className="animate-pulse">
                      <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                    </div>
                  ))}
                </div>
              ) : trendingPosts.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400">No trending posts yet</p>
              ) : (
                <div className="space-y-4">
                  {trendingPosts.map((post, index) => (
                    <Link
                      key={post.id}
                      to={`/blog/post/${post.slug}`}
                      className="block group"
                    >
                      {index === 0 ? (
                        // #1 Featured Trending Post - Large with Image
                        <Card className="overflow-hidden hover:shadow-2xl transition-all border-2 border-orange-500 dark:bg-gray-800 dark:border-orange-500 relative">
                          <div className="absolute top-3 left-3 z-10 w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center shadow-lg">
                            <span className="text-2xl font-black">1</span>
                          </div>
                          <CardContent className="p-0">
                            <div className="relative aspect-video overflow-hidden bg-gray-100 dark:bg-gray-900">
                              <img 
                                src={getImageUrl(post.cover_image)} 
                                alt={post.title}
                                className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/800x450?text=No+Image';
                                }}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                              <div className="absolute bottom-0 left-0 right-0 p-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded shadow-lg">
                                    👁️ MOST VIEWED
                                  </span>
                                  <span className="flex items-center gap-1 text-white text-xs font-semibold bg-black/40 px-2 py-1 rounded">
                                    <Eye className="w-3 h-3" />
                                    {post.views || 0}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="p-4">
                              <h3 className="font-bold text-base mb-2 line-clamp-2 group-hover:text-orange-500 dark:text-gray-100 transition-colors">
                                {post.title}
                              </h3>
                              <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {post.reading_time || 5} min read
                                </span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ) : (
                        // #2-5 Compact with Thumbnail
                        <Card className="overflow-hidden hover:shadow-lg transition-all border-l-4 border-orange-500 dark:bg-gray-800 dark:border-gray-700">
                          <CardContent className="p-3">
                            <div className="flex gap-3">
                              <div className="flex-shrink-0">
                                <span className="text-2xl font-black text-orange-500">
                                  {index + 1}
                                </span>
                              </div>
                              <div className="flex-shrink-0 w-16 h-16 rounded overflow-hidden">
                                <img 
                                  src={getImageUrl(post.cover_image)} 
                                  alt={post.title}
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/100x100?text=No+Image';
                                  }}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-bold text-sm mb-1 line-clamp-2 group-hover:text-orange-500 dark:text-gray-100 transition-colors">
                                  {post.title}
                                </h3>
                                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                  <span className="flex items-center gap-1">
                                    <Eye className="w-3 h-3" />
                                    {post.views || 0}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {post.reading_time || 5} min
                                  </span>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Latest Posts (2/3) */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <ArrowRight className="w-6 h-6 text-red-500" />
              <h2 className="text-2xl font-bold dark:text-gray-100">LATEST POSTS</h2>
              {searchTerm && (
                <Badge className="bg-red-600 text-white">
                  {filteredLatestPosts.length} result{filteredLatestPosts.length !== 1 ? 's' : ''} for "{searchTerm}"
                </Badge>
              )}
            </div>
            
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 dark:border-purple-400 mx-auto"></div>
                <p className="mt-4 text-gray-600 dark:text-gray-400">Loading articles...</p>
              </div>
            ) : filteredLatestPosts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 dark:text-gray-400 text-lg">
                  {searchTerm ? `No results found for "${searchTerm}"` : 'No articles found. Please check back later!'}
                </p>
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm('')}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                  >
                    Clear Search
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredLatestPosts.map((post) => (
                  <Card 
                    key={post.id} 
                    id={`post-${post.id}`}
                    className={`overflow-hidden hover:shadow-xl transition-all transform hover:scale-105 dark:bg-gray-800 dark:border-gray-700 ${
                      highlightedPostId === post.id 
                        ? 'ring-4 ring-purple-500 ring-offset-4 ring-offset-white dark:ring-offset-gray-900 animate-pulse scale-105 shadow-2xl' 
                        : ''
                    }`}
                  >
                    <CardContent className="p-0">
                      <div className="relative aspect-video bg-gray-100 dark:bg-gray-900">
                        <img 
                          src={getImageUrl(post.cover_image)} 
                          alt={post.title} 
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/800x600?text=No+Image';
                          }}
                        />
                        {post.status === 'published' && new Date(post.published_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) && (
                          <Badge className="absolute top-3 right-3 bg-gradient-to-r from-green-400 to-blue-500 text-white">New</Badge>
                        )}
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDate(post.published_at || post.created_at)}
                          </span>
                        </div>
                        <h3 className="font-bold text-lg mb-2 line-clamp-2 hover:text-red-600 dark:hover:text-red-400 dark:text-gray-100">
                          <Link to={`/blog/post/${post.slug}`}>{post.title}</Link>
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">{post.excerpt}</p>
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                          <span>{post.author_name || 'Admin'}</span>
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {post.reading_time || 5} min
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {post.views || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="mt-16">
          <NewsletterSubscribe />
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlogHomePage;